import { Layouts } from '../../../controls/layouts';
import { Component, inject, input, signal, viewChild, OnInit, resource, computed, linkedSignal, effect, ChangeDetectionStrategy } from '@angular/core';
import { StakeholderService } from '../../../stakeholder/services/stakeholder.service';
import { AlertConfiguration } from '../../models/alert-configuration';
import { ScheduleItemComponent } from '../../../shared/scheduling/schedule-item.component';
import { FormsModule } from '@angular/forms';
import { MailTemplateEditComponent } from '../../../configuration/mail-template-edit/mail-template-edit.component';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { MailTemplatesService } from 'src/app/mail-templates/services/mail-templates.service';
import { MailTemplate } from 'src/app/mail-templates/models/mail-template';
import { MacroDefinition } from 'src/app/mail-templates/models/macro-definition';
import { MacroObjectType } from 'src/app/mail-templates/models/macro-object-type';
import { PartnerContactService } from 'src/app/partner/services/partner-contact.service';
import { PartnerContactType } from 'src/app/partner/models';
import { DxDataGridModule, DxTagBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { DxiColumnModule, DxiValidationRuleModule, DxoColumnFixingModule, DxoGroupPanelModule } from 'devextreme-angular/ui/nested';
import { ScheduleItem } from 'src/app/shared/scheduling/models';


@Component({
    selector: 'app-alert-configuration-edit',
    templateUrl: './alert-configuration-edit.component.html',
    styleUrls: ['./alert-configuration-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, MailTemplateEditComponent, ScheduleItemComponent, Layouts,
        DxTagBoxModule, BizDataGridDirective, DxoColumnFixingModule, DxoGroupPanelModule,
        DxiColumnModule, DxiValidationRuleModule, DxTagBoxModule, DxDataGridModule]
})
export class AlertConfigurationEditComponent implements OnInit {
    //Public properties
    public PartnerContactTypesEnum = ContactPartnerContactTypesEnum;

    //ViewChild
    private alertSchedule = viewChild.required<ScheduleItemComponent>('alertSchedule');

    //Inject
    private stakeholderService = inject(StakeholderService);
    private mailTemplateService = inject(MailTemplatesService);
    private partnerContactService = inject(PartnerContactService)
    private partnerService = inject(PartnerContactService);

    //Input
    alertConfiguration = input<AlertConfiguration>(null);
    isReadOnly = input<boolean>(false);
    senderPartner = input<number>(0);
    receivingPartner = input<number>(0);

    //signals
    stakeholders = signal<Stakeholder[]>([]);
    mailTemplate = signal<MailTemplate>(null);
    macroDefinitions = signal<MacroDefinition[]>([]);
    contactTypes = signal<PartnerContactType[]>([]);

    currentReceivingPartnerContactTypes = linkedSignal(() => this.alertConfiguration()?.ReceivingPartnerContactTypes ?? []);
    currentSenderPartnerContactTypes = linkedSignal(() => this.alertConfiguration()?.SenderPartnerContactTypes ?? []);
    selectedTriggerCount = linkedSignal(() => this.alertConfiguration()?.ScheduleItem?.Triggers?.length ?? 0);

    selectedReceivingPartnerContactsResource = resource({
        params: () => ({
            partnerId: this.receivingPartner(),
            selectedPartnerContactTypes: this.currentReceivingPartnerContactTypes()
        }),
        loader: ({ params }) => this.getPartnerContats(params.partnerId, params.selectedPartnerContactTypes)
    });
    selectedReceivingPartnerContacts = computed(() => this.selectedReceivingPartnerContactsResource.value() ?? []);

    selectedSenderPartnerContactsResource = resource({
        params: () => ({
            partnerId: this.senderPartner(),
            selectedPartnerContactTypes: this.currentSenderPartnerContactTypes()
        }),
        loader: ({ params }) => this.getPartnerContats(params.partnerId, params.selectedPartnerContactTypes)
    });
    selectedSenderPartnerContacts = computed(() => this.selectedSenderPartnerContactsResource.value() ?? []);

    // Convert this to signal whenever we can.
    get isScheduleValid(): boolean {
        return this.alertSchedule().isScheduleValid;
    };

    ngOnInit(): void {
        // Initialize the local state with values from the database
        void this.refresh();
    }

    //Event handlers
    onOverrideTemplateChange(value: boolean): void {
        this.alertConfiguration().OverrideMailTemplate = value;
        if(this.alertConfiguration().OverrideMailTemplate)
        {
            this.alertConfiguration().OverrideMailSubject ??= this.mailTemplate()?.UseCustom ? this.mailTemplate()?.CustomSubject : this.mailTemplate()?.Subject;
            this.alertConfiguration().OverrideMailBody ??= this.mailTemplate()?.UseCustom ? this.mailTemplate()?.CustomBody : this.mailTemplate()?.Body;
        }
    }

    onStakeholderChanged(value: number): void {
        this.alertConfiguration().StakeholderId = value;
        if(value === 0) {
            this.alertConfiguration().NotifyStakeholder = false;
            this.alertConfiguration().IsUrgent = false;
        } else {
            this.alertConfiguration().NotifyStakeholder = true;
        }
    }

    onPartnerContactTypesChange(type: ContactPartnerContactTypesEnum, selectedValues: number[]): void {
        if (type === ContactPartnerContactTypesEnum.Receiving) {
            this.alertConfiguration().ReceivingPartnerContactTypes = selectedValues;
            this.currentReceivingPartnerContactTypes.set(selectedValues);
        } else if (type === ContactPartnerContactTypesEnum.Sender) {
            this.alertConfiguration().SenderPartnerContactTypes = selectedValues;
            this.currentSenderPartnerContactTypes.set(selectedValues);
        }
    }

    onScheduleTriggerChange(updatedScheduleItem: ScheduleItem): void {
        this.selectedTriggerCount.set(updatedScheduleItem?.Triggers?.length ?? 0); // Update the trigger count
    }

    async refresh(): Promise<void> {
        const [stakeholders, mailTemplate, macroDefinitions, contactTypes] = await Promise.all([
            this.stakeholderService.getStakeholders() ?? [],
            this.mailTemplateService.getMailTemplateByKey('AlertDefault'),
            this.mailTemplateService.getMacroDefinitions(MacroObjectType.AlertConfiguration) ?? [],
            this.partnerContactService.getContactTypes() ?? []
        ]);

        this.stakeholders.set(stakeholders);
        this.mailTemplate.set(mailTemplate);
        this.macroDefinitions.set(macroDefinitions);
        this.contactTypes.set(contactTypes);
    }

    async getPartnerContats(partnerId: number, selectedPartnerContactTypes: number[]): Promise<PartnerContacts[]> {
        const partnerContacts: PartnerContacts[] = [];
        const partnerContats = await this.partnerService.getContactsForPartner(partnerId ?? 0);
        const contactTypes = await this.partnerContactService.getContactTypes();

        for (const contact of partnerContats) {
            // Check if any of the contact's types match the selected types
            const hasMatchingType = contact.ContactTypes.some((type) => selectedPartnerContactTypes.includes(type.Id));

            if (hasMatchingType) {
                // Map all the contact's types to their names
                const allTypes = contact.ContactTypes
                    .map((type) => contactTypes.find((ct) => ct.Id === type.Id)?.Name)
                    .filter((typeName) => typeName); // Remove undefined values

                partnerContacts.push({
                    PartnerContactTypeId: contact.ContactsId,
                    ContactName: contact.Name,
                    Email: contact.EmailAddress,
                    Type: allTypes.join(', ') // Join all types into a single string
                });
            }
        }

        return partnerContacts;
    }
}

export enum ContactPartnerContactTypesEnum {
    Receiving = 'Receiving',
    Sender = 'Sender'
}

export class PartnerContacts {
    PartnerContactTypeId: number;
    ContactName: string;
    Email: string;
    Type: string;
}
